// ✅ src/pages/AlertsDashboard.jsx
import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ThemeContext } from "../context/ThemeContext";
import getCurrentSkin from "../utils/getCurrentSkin";
import HeaderBar from "../components/HeaderBar";
import SalesLayoutWrapper from "../components/SalesLayoutWrapper";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaChevronUp,
  FaChevronDown,
  FaCalendarAlt,
  FaCreditCard,
} from "react-icons/fa";

const BASE_URL = import.meta?.env?.VITE_BASE_URL || "http://nissi-india.com";

const AlertsDashboard = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const [showFilters, setShowFilters] = useState(true);

  // ✅ localStorage helper
  const getLS = (key, fallback = "") => {
    if (typeof window === "undefined") return fallback;
    const val = localStorage.getItem(key);
    return val !== null && val !== undefined ? val : fallback;
  };

  // ✅ localStorage values
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchId = getLS("branchId") || getLS("branchid");
  const branch = getLS("branchType") || getLS("branchtype");
  const userId = getLS("userId") || getLS("loginuserid") || getLS("loginUserId");

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  // ✅ Filters
  const [days, setDays] = useState(10);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [alertType, setAlertType] = useState("");

  // ✅ Data
  const [emiAlerts, setEmiAlerts] = useState([]);
  const [birthdayAlerts, setBirthdayAlerts] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      setError("");

      const res = await axios.get(`${BASE_URL}/api/salesDashboard/all`, {
        headers: commonHeaders,
        params: {
          days,
          customerName: customerName?.trim() || "",
          fromDate: fromDate || "",
          toDate: toDate || "",
        },
      });

      const payload = res.data?.data || {};
      setEmiAlerts(payload.emiAlerts || []);
      setBirthdayAlerts(payload.birthdayAlerts || []);
    } catch (err) {
      console.error("Dashboard fetch error:", err);
      const msg =
        err?.response?.data?.message || err.message || "Failed to load dashboard";
      setError(msg);
      toast.error(msg);
      setEmiAlerts([]);
      setBirthdayAlerts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visibleEmi = useMemo(() => {
    if (alertType === "birthday") return [];
    return emiAlerts;
  }, [alertType, emiAlerts]);

  const visibleBirthday = useMemo(() => {
    if (alertType === "emi") return [];
    return birthdayAlerts;
  }, [alertType, birthdayAlerts]);

  const totalEmi = emiAlerts.length;
  const totalBirthday = birthdayAlerts.length;

  const clearFilters = () => {
    setDays(10);
    setFromDate("");
    setToDate("");
    setCustomerName("");
    setAlertType("");
    setTimeout(fetchDashboard, 0);
  };

  return (
    <SalesLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">ALERTS DASHBOARD</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6">
            <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              Alerts Dashboard
            </h1>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowFilters((s) => !s)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <button
                onClick={fetchDashboard}
                className={`px-4 py-2 rounded-lg font-semibold text-white shadow-lg hover:shadow-xl transform transition duration-200 bg-${baseColor}-${baseShade} hover:bg-${baseColor}-700`}
              >
                Refresh
              </button>
            </div>
          </div>

          {showFilters && (
            <div className={`p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-blue-900"}`}>
                  Filter Options
                </h3>

                <button
                  onClick={clearFilters}
                  className={`px-3 py-2 rounded-lg text-sm font-semibold border ${
                    isDark
                      ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                      : "border-gray-300 text-gray-700 hover:bg-white"
                  }`}
                >
                  Clear
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    EMI Upcoming Days
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={days}
                    onChange={(e) => setDays(Number(e.target.value || 0))}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    From Date
                  </label>
                  <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    To Date
                  </label>
                  <input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Customer Name
                  </label>
                  <input
                    type="text"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    placeholder="Search customer..."
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Alert Type
                  </label>
                  <select
                    value={alertType}
                    onChange={(e) => setAlertType(e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="">All Alerts</option>
                    <option value="emi">EMI Alerts</option>
                    <option value="birthday">Birthday Alerts</option>
                  </select>
                </div>

                <div className="md:col-span-2 flex items-end">
                  <button
                    onClick={fetchDashboard}
                    disabled={loading}
                    className={`w-full px-4 py-2 rounded-lg font-semibold text-white shadow-lg hover:shadow-xl transform transition duration-200 ${
                      loading ? "opacity-60 cursor-not-allowed" : ""
                    } bg-${baseColor}-${baseShade} hover:bg-${baseColor}-700`}
                  >
                    {loading ? "Loading..." : "Apply Filters"}
                  </button>
                </div>
              </div>

              {error && (
                <div className={`mt-4 text-sm ${isDark ? "text-red-300" : "text-red-600"}`}>
                  {error}
                </div>
              )}
            </div>
          )}

          <div className="mb-8">
            <div className={`flex items-center gap-3 mb-4 p-4 rounded-lg ${isDark ? "bg-blue-900/30" : "bg-blue-100"}`}>
              <FaCreditCard className={`text-2xl ${isDark ? "text-blue-300" : "text-blue-600"}`} />
              <h2 className={`text-xl font-bold ${isDark ? "text-white" : "text-blue-900"}`}>EMI ALERTS</h2>
              <span className={`ml-auto text-sm font-semibold ${isDark ? "text-blue-200" : "text-blue-700"}`}>
                {visibleEmi.length} record(s)
              </span>
            </div>

            <div className="overflow-x-auto rounded-lg shadow-lg">
              <table className={`w-full border-collapse ${isDark ? "bg-gray-800" : "bg-white"}`}>
                <thead>
                  <tr className={`${isDark ? "bg-blue-900 text-white" : "bg-blue-600 text-white"}`}>
                    <th className="px-6 py-3 text-left text-sm font-semibold uppercase tracking-wider border-r border-blue-700">Sl No</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold uppercase tracking-wider border-r border-blue-700">Enquiry Number</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold uppercase tracking-wider border-r border-blue-700">Customer Name</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold uppercase tracking-wider border-r border-blue-700">Customer ID</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold uppercase tracking-wider border-r border-blue-700">Payment Mode</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold uppercase tracking-wider">EMI Date</th>
                  </tr>
                </thead>

                <tbody className={`${isDark ? "bg-gray-800 divide-gray-700" : "bg-white divide-gray-200"} divide-y`}>
                  {visibleEmi.length === 0 ? (
                    <tr>
                      <td colSpan="6" className={`px-6 py-8 text-center ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                        No Records Found!
                      </td>
                    </tr>
                  ) : (
                    visibleEmi.map((alert, idx) => (
                      <tr
                        key={`${alert.customerId}-${alert.enquiryNumber}-${idx}`}
                        className={`hover:${isDark ? "bg-gray-700" : "bg-gray-50"} transition-colors duration-200`}
                      >
                        <td className={`px-6 py-4 text-sm whitespace-nowrap ${isDark ? "text-white border-r border-gray-700" : "text-gray-900 border-r border-gray-200"}`}>
                          {alert.slNo}
                        </td>
                        <td className={`px-6 py-4 text-sm whitespace-nowrap ${isDark ? "text-white border-r border-gray-700" : "text-gray-900 border-r border-gray-200"}`}>
                          {alert.enquiryNumber}
                        </td>
                        <td className={`px-6 py-4 text-sm whitespace-nowrap font-medium ${isDark ? "text-white border-r border-gray-700" : "text-gray-900 border-r border-gray-200"}`}>
                          {alert.customerName}
                        </td>
                        <td className={`px-6 py-4 text-sm whitespace-nowrap ${isDark ? "text-white border-r border-gray-700" : "text-gray-900 border-r border-gray-200"}`}>
                          {alert.customerId}
                        </td>
                        <td className={`px-6 py-4 text-sm whitespace-nowrap ${isDark ? "text-white border-r border-gray-700" : "text-gray-900 border-r border-gray-200"}`}>
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-orange-100 text-orange-800">
                            {alert.paymentMode}
                          </span>
                        </td>
                        <td className={`px-6 py-4 text-sm whitespace-nowrap ${isDark ? "text-white" : "text-gray-900"}`}>
                          {alert.emiDate}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="mb-8">
            <div className={`flex items-center gap-3 mb-4 p-4 rounded-lg ${isDark ? "bg-green-900/30" : "bg-green-100"}`}>
              <FaCalendarAlt className={`text-2xl ${isDark ? "text-green-300" : "text-green-600"}`} />
              <h2 className={`text-xl font-bold ${isDark ? "text-white" : "text-green-900"}`}>BIRTHDAY ALERTS</h2>
              <span className={`ml-auto text-sm font-semibold ${isDark ? "text-green-200" : "text-green-700"}`}>
                {visibleBirthday.length} record(s)
              </span>
            </div>

            <div className="overflow-x-auto rounded-lg shadow-lg">
              <table className={`w-full border-collapse ${isDark ? "bg-gray-800" : "bg-white"}`}>
                <thead>
                  <tr className={`${isDark ? "bg-green-900 text-white" : "bg-green-600 text-white"}`}>
                    <th className="px-6 py-3 text-left text-sm font-semibold uppercase tracking-wider border-r border-green-700">Sl No</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold uppercase tracking-wider border-r border-green-700">Customer Name</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold uppercase tracking-wider border-r border-green-700">Customer ID</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold uppercase tracking-wider">Birthday Date</th>
                  </tr>
                </thead>

                <tbody className={`${isDark ? "bg-gray-800 divide-gray-700" : "bg-white divide-gray-200"} divide-y`}>
                  {visibleBirthday.length === 0 ? (
                    <tr>
                      <td colSpan="4" className={`px-6 py-8 text-center ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                        <div className="flex flex-col items-center gap-2">
                          <FaCalendarAlt className="text-4xl opacity-50" />
                          <span className="text-lg font-medium">No Records Found!</span>
                          <span className="text-sm">There are no birthday alerts for today.</span>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    visibleBirthday.map((alert, idx) => (
                      <tr
                        key={`${alert.customerId}-${alert.enquiryNumber}-${idx}`}
                        className={`hover:${isDark ? "bg-gray-700" : "bg-gray-50"} transition-colors duration-200`}
                      >
                        <td className={`px-6 py-4 text-sm whitespace-nowrap ${isDark ? "text-white border-r border-gray-700" : "text-gray-900 border-r border-gray-200"}`}>
                          {alert.slNo}
                        </td>
                        <td className={`px-6 py-4 text-sm whitespace-nowrap font-medium ${isDark ? "text-white border-r border-gray-700" : "text-gray-900 border-r border-gray-200"}`}>
                          {alert.customerName}
                        </td>
                        <td className={`px-6 py-4 text-sm whitespace-nowrap ${isDark ? "text-white border-r border-gray-700" : "text-gray-900 border-r border-gray-200"}`}>
                          {alert.customerId}
                        </td>
                        <td className={`px-6 py-4 text-sm whitespace-nowrap ${isDark ? "text-white" : "text-gray-900"}`}>
                          {alert.birthdayDate}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className={`p-6 rounded-lg border ${isDark ? "bg-blue-900/20 border-blue-700" : "bg-blue-50 border-blue-200"}`}>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className={`text-lg font-semibold ${isDark ? "text-blue-300" : "text-blue-900"}`}>Total EMI Alerts</h3>
                  <p className={`text-3xl font-bold ${isDark ? "text-white" : "text-blue-600"}`}>{totalEmi}</p>
                </div>
                <FaCreditCard className={`text-4xl ${isDark ? "text-blue-300" : "text-blue-600"}`} />
              </div>
            </div>

            <div className={`p-6 rounded-lg border ${isDark ? "bg-green-900/20 border-green-700" : "bg-green-50 border-green-200"}`}>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className={`text-lg font-semibold ${isDark ? "text-green-300" : "text-green-900"}`}>Total Birthday Alerts</h3>
                  <p className={`text-3xl font-bold ${isDark ? "text-white" : "text-green-600"}`}>{totalBirthday}</p>
                </div>
                <FaCalendarAlt className={`text-4xl ${isDark ? "text-green-300" : "text-green-600"}`} />
              </div>
            </div>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </SalesLayoutWrapper>
  );
};

export default AlertsDashboard;